import {
  NavLink,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useState, useEffect } from "react";

// styles and images
import "./Project.css";
import Archive from "../../assets/archive.svg";
import Show from "../../assets/show_more.svg";
import Hide from "../../assets/show_less.svg";
import Refresh from "../../assets/refresh.svg";

// components
import ProjectSummary from "./ProjectSummary";
import ProjectComments from "./ProjectComments";
import CreateProject from "../dashboard/CreateProject";
import useGetFetch from "../../hooks/useGetFetch";
import Loading from "../../components/Loading";
import SandPoPopup from "./SandPoPopup";
import ProjectSand from "./ProjectSand";
import LockLoad from "./LockLoad";
import ProjectTotals from "./ProjectTotals";
import FiscalTotals from "./FiscalTotals";
import ArchiveProject from "./ArchiveProject";
import ErrorDisplay from "../../components/ErrorDisplay";
import AddCarrier from "./AddCarrier";
import CarrierCard from "./CarrierCard";
import DeletePopup from "../../components/DeletePopup";
import LoadTabs from "./LoadTabs";
import CreditDebitCard from "./CreditDebitCard";
import AddCreditDebit from "./AddCreditDebit";

// hooks
import { useGetProjectDD } from "../../hooks/useGetProjectDD";
import { useGetLoadOptions } from "../../hooks/useGetLoadOptions";

// global
import {
  accountDataStore,
  messageAndErrorStore,
  projectStore,
  selectOptionsStore,
  userStore,
} from "../../store/Store";
import PoTotals from "./PoTotals";
import TerminalMileagesCard from "./TerminalMileagesCard";
import AddProjectRateSheet from "./AddProjectRateSheet";

export default function Project() {
  // global
  const currentCompany = userStore((data) => data.currentCompany);
  const currentUser = userStore((data) => data.currentUser);
  const JwtToken = userStore((data) => data.JwtToken);
  const setAuthStatus = userStore((data) => data.setAuthStatus);
  const setCurrentProject = projectStore((data) => data.setCurrentProject);
  const currentProject = projectStore((data) => data.currentProject);
  const setProjectLoads = projectStore((data) => data.setProjectLoads);
  const projectLoads = projectStore((data) => data.projectLoads);
  const setProjectAssetLoads = projectStore(
    (data) => data.setProjectAssetLoads,
  );
  const setProjectSandRates = projectStore((data) => data.setProjectSandRates);
  const setProjectCarriers = projectStore((data) => data.setProjectCarriers);
  const projectCarriers = projectStore((data) => data.projectCarriers);
  const setProjectUnaddedCarriers = projectStore(
    (data) => data.setProjectUnaddedCarriers,
  );
  const projectUnaddedCarriers = projectStore(
    (data) => data.projectUnaddedCarriers,
  );
  const setProjectTotals = projectStore((data) => data.setProjectTotals);
  const setProjectCreditsDebits = projectStore(
    (data) => data.setProjectCreditsDebits,
  );
  const setProjectAccruals = projectStore((data) => data.setProjectAccruals);
  const setProjectCustomerInvoices = projectStore(
    (data) => data.setProjectCustomerInvoices,
  );
  const setProjectVendorInvoices = projectStore(
    (data) => data.setProjectVendorInvoices,
  );
  const setProjectRateSheets = projectStore(
    (data) => data.setProjectRateSheets,
  );
  const setCarrierOptions = selectOptionsStore(
    (data) => data.setCarrierOptions,
  );
  const carrierOptions = selectOptionsStore((data) => data.carrierOptions);
  const setTerminalOptions = selectOptionsStore(
    (data) => data.setTerminalOptions,
  );
  const setPurchaseOrderOptions = selectOptionsStore(
    (data) => data.setPurchaseOrderOptions,
  );
  const purchaseOrderOptions = selectOptionsStore(
    (data) => data.purchaseOrderOptions,
  );
  const setAccountCustomerInvoices = accountDataStore(
    (data) => data.setAccountCustomerInvoices,
  );
  const toggleNewMessage = messageAndErrorStore(data => data.toggleNewMessage)

  // triggers
  const [editIsTriggered, setEditIsTriggered] = useState(false);
  const [hideComments, setHideComments] = useState(true);
  const [sandIsTriggered, setSandIsTriggered] = useState(false);
  const [deleteTrigger, setDeleteTrigger] = useState(false);
  const [addCarrierTrigger, setAddCarrierTrigger] = useState(false);
  const [loadLockTrigger, setLoadLockTrigger] = useState(false);
  const [isAsset, setIsAsset] = useState(false);
  const [lockEndpoint, setLockEndpoint] = useState(null);
  const [showPTotals, setShowPTotals] = useState(true);
  const [showFTotals, setShowFTotals] = useState(true);
  const [creditDebitTrigger, setCreditDebitTrigger] = useState(false);
  const [archiveTrigger, setArchiveTrigger] = useState(false);
  const [addProjectRsTrigger, setAddProjectRsTrigger] = useState(false);

  // local
  const [formError, setFormError] = useState(null);
  const [editId, setEditId] = useState(null);
  const [deleteFields, setDeleteFields] = useState(null);
  const [loadsPending, setLoadsPending] = useState(false);
  const [assetLoadsPending, setAssetLoadsPending] = useState(false);
  const [error, setError] = useState(null);
  const [terminalMileages, setTerminalMileages] = useState([]);
  const { id } = useParams();
  const navigate = useNavigate();

  // dropdowns
  const { dropDowns } = useGetProjectDD();
  // ---------------- use store to add these options -----------
  useGetLoadOptions(setPurchaseOrderOptions, "po_number", "po_id");
  useGetLoadOptions(setCarrierOptions, "carrier_name", "pc_carrier_id");
  useGetLoadOptions(setTerminalOptions, "terminal_name", "id");

  // get data
  const { isPending: projectPending, error: projectError } = useGetFetch(
    `${process.env.REACT_APP_BACKEND}/pc/project/${id}`,
    setCurrentProject,
  );
  const { isPending: sandRatesPending, error: sandRatesError } = useGetFetch(
    `${process.env.REACT_APP_BACKEND}/pc/sand-rates/${id}`,
    setProjectSandRates,
  );
  const { isPending: carriersPending, error: carriersError } = useGetFetch(
    `${process.env.REACT_APP_BACKEND}/pc/project/carriers/${id}`,
    setProjectCarriers,
  );
  const { isPending: customerInvsPending, error: customerInvError } =
    useGetFetch(
      `${process.env.REACT_APP_BACKEND}/pc/customer-invoices/${id}`,
      setProjectCustomerInvoices,
    );
  const { isPending: vendorInvsPending, error: vendorInvError } = useGetFetch(
    `${process.env.REACT_APP_BACKEND}/pc/vendor-invoices/${id}`,
    setProjectVendorInvoices,
  );
  const { isPending: creditDebitPending, error: creditDebitError } =
    useGetFetch(
      `${process.env.REACT_APP_BACKEND}/pc/project-credits-debits/${id}`,
      setProjectCreditsDebits,
    );
  const { isPending: accrualsPending, error: accrualsError } = useGetFetch(
    `${process.env.REACT_APP_BACKEND}/pc/project-accruals/${id}`,
    setProjectAccruals,
  );
  const { error: unaddedCarriersError, isPending: unaddedCarriersisPending } =
    useGetFetch(
      `${process.env.REACT_APP_BACKEND}/pc/unadded-carriers/${id}`,
      setProjectUnaddedCarriers,
    );
  const { error: terminalsError, isPending: terminalsPending } = useGetFetch(
    `${process.env.REACT_APP_BACKEND}/pc/terminal-mileages/${id}`,
    setTerminalMileages,
  );
  const { error: pRsError, isPending: pRsPending } = useGetFetch(
    `${process.env.REACT_APP_BACKEND}/pc/all-project-rate-sheets/${id}`,
    setProjectRateSheets,
  );

  useEffect(() => {
    if (currentCompany.full_access) {
      const controller = new AbortController();
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("Authorization", "Bearer " + JwtToken);
      const requestOptions = {
        method: "GET",
        headers,
        signal: controller.signal,
      };

      fetch(
        `${process.env.REACT_APP_BACKEND}/pc/account-customer-invoices`,
        requestOptions,
      )
        .then((res) => {
          if (!res.ok) {
            if (res.status === 401) {
              setAuthStatus(false);
              navigate("/login");
              return Promise.resolve();
            } else {
              setError("Something went wrong");
              return Promise.resolve();
            }
          } else {
            return res.json();
          }
        })
        .then((data) => {
          if (!data) {
            return;
          } else {
            setAccountCustomerInvoices(data.customer_invoices);
            return;
          }
        })
        .catch((err) => {
          setError("Something went wrong");
          console.log(err);
          return;
        });
      return () => controller.abort();
    }
  }, [
    currentCompany,
    navigate,
    JwtToken,
    setAccountCustomerInvoices,
    setAuthStatus,
  ]);
  // update loads
  useEffect(() => {
    setLoadsPending(true);
    const controller = new AbortController();
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);
    const requestOptions = {
      method: "GET",
      headers,
      signal: controller.signal,
    };

    fetch(
      `${process.env.REACT_APP_BACKEND}/pc/loads?project_id=${id}&user_id=${currentUser.id}`,
      requestOptions,
    )
      .then((res) => {
        if (res.status === 401) {
          setAuthStatus(false);
          navigate("/login");
        } else {
          return res.json();
        }
      })
      .then((data) => {
        if (data.error) {
          setFormError(data.message);
          setError("Could not get Loads");
          setLoadsPending(false);
          toggleNewMessage("Could not get loads");
        } else {
          if (data.eia_error !== "") {
            toggleNewMessage(data.eia_error);
          }
          setProjectLoads(data.loads);
          setProjectTotals(data.all_totals);
          setLoadsPending(false);
        }
      })
      .catch((err) => {
        if (err?.name !== "Abort Error") console.log(err);
        setLoadsPending(false);
      });
    return () => {
      controller.abort();
    };
  }, [
    currentCompany,
    currentUser,
    JwtToken,
    setProjectLoads,
    setFormError,
    setLoadsPending,
    setProjectTotals,
    id,
    navigate,
    setAuthStatus,
    toggleNewMessage,
  ]);

  // update asset loads
  useEffect(() => {
    setLoadsPending(true);
    const controller = new AbortController();
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);
    const requestOptions = {
      method: "GET",
      headers,
      signal: controller.signal,
    };

    fetch(
      `${process.env.REACT_APP_BACKEND}/pc/asset-loads?project_id=${id}&user_id=${currentUser.id}`,
      requestOptions,
    )
      .then((res) => {
        if (res.status === 401) {
          setAuthStatus(false);
          navigate("/login");
        } else {
          return res.json();
        }
      })
      .then((data) => {
        if (data.error) {
          setFormError(data.message);
          setError("Could not get Asset Loads");
          setAssetLoadsPending(false);
          toggleNewMessage("Could not get asset loads");
        } else {
          if (data.eia_error !== "") {
            toggleNewMessage(data.eia_error);
          }
          setProjectAssetLoads(data.loads);
          setProjectTotals(data.all_totals);
          setAssetLoadsPending(false);
        }
      })
      .catch((err) => {
        console.log(err);
        setAssetLoadsPending(false);
      });
    return () => controller.abort();
  }, [
    currentCompany,
    currentUser,
    JwtToken,
    setProjectAssetLoads,
    setFormError,
    setAssetLoadsPending,
    setProjectTotals,
    id,
    navigate,
    setAuthStatus,
    toggleNewMessage,
  ]);

  if (
    projectError ||
    formError ||
    sandRatesError ||
    carriersError ||
    customerInvError ||
    vendorInvError ||
    creditDebitError ||
    accrualsError ||
    unaddedCarriersError ||
    terminalsError ||
    pRsError ||
    error
  ) {
    return (
      <ErrorDisplay message={"Oops! Something Went Wrong 😓"} code={500} />
    );
  }

  const refreshLoads = () => {
    setLoadsPending(true);
    const controller = new AbortController();
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Authorization", "Bearer " + JwtToken);
    const requestOptions = {
      method: "GET",
      headers,
      signal: controller.signal,
    };

    fetch(
      `${process.env.REACT_APP_BACKEND}/pc/project-refresh/${currentProject.project.id}?user_id=${currentUser.id}`,
      requestOptions,
    )
      .then((res) => {
        if (res.status === 401) {
          setAuthStatus(false);
          navigate("/login");
        } else {
          return res.json();
        }
      })
      .then((data) => {
        if (data.error) {
          toggleNewMessage(data.message);
          setError("Could not refresh loads");
          setLoadsPending(false);
        } else {
          setProjectLoads(data.loads);
          setProjectAssetLoads(data.asset_loads);
          setProjectTotals(data.all_totals);
          setLoadsPending(false);
        }
      })
      .catch((err) => {
        console.log(err);
        toggleNewMessage("Something Went Wrong");
        setLoadsPending(false);
      });
    return () => controller.abort();
  };
  
  return currentProject &&
    !projectPending &&
    !sandRatesPending &&
    !carriersPending &&
    !creditDebitPending &&
    !accrualsPending &&
    !unaddedCarriersisPending &&
    !terminalsPending &&
    !pRsPending &&
    !loadsPending ? (
    <div className="container-fluid">
      <div className="d-flex justify-content-between project-header border-bottom">
        <h2>{currentProject.project.job_name}</h2>
        {currentProject &&
          projectLoads &&
          !customerInvsPending &&
          !loadsPending &&
          !vendorInvsPending && (
            <div className="d-flex justify-content-between project-header">
              <NavLink
                to={`/project/${id}/invoices`}
                className="invoice-link"
              >
                Project Invoices
              </NavLink>
              <NavLink to={`/project/${id}/accruals`} className="invoice-link">
                Accruals
              </NavLink>
            </div>
          )}
        <div>
          {!hideComments && (
            <button
              className="btn btn-secondary"
              onClick={() => setHideComments(true)}
            >
              Hide Comments
            </button>
          )}
          {hideComments && (
            <button
              className="btn btn-secondary"
              onClick={() => setHideComments(false)}
            >
              Show Comments
            </button>
          )}
          {!currentProject.project.archived && (
            <img
              src={Refresh}
              className="project-delete-image"
              onClick={refreshLoads}
              alt="refresh"
            />
          )}
          <img
            src={Archive}
            onClick={() => setArchiveTrigger(true)}
            className="project-delete-image"
            alt="archive"
          />
        </div>
      </div>
      <div className="row g-2 totals">
        <div className="d-flex justify-content-between card-header">
          <h4>Project Specifications</h4>
          {!currentProject.project.archived && (
            <div>
              {projectLoads && (
                <button
                  className="btn btn-secondary"
                  onClick={() => setAddCarrierTrigger(true)}
                >
                  Add Carrier
                </button>
              )}
              {projectLoads && (
                <button
                  className="btn btn-secondary"
                  onClick={() => setSandIsTriggered(true)}
                >
                  Add Sand POs
                </button>
              )}
            </div>
          )}
        </div>
        <ProjectSummary
          project={currentProject.project}
          setEditIsTriggered={setEditIsTriggered}
          isArchive={currentProject.project.archived}
        />
        {(projectCarriers ||
          (projectUnaddedCarriers && projectUnaddedCarriers.length > 0)) &&
          carrierOptions &&
          projectLoads && (
            <CarrierCard
              setEditCarrier={setAddCarrierTrigger}
              setDeleteCarrier={setDeleteTrigger}
              setCarrierId={setEditId}
              setDeleteFields={setDeleteFields}
            />
          )}
        {
          <ProjectSand
            setEditTrigger={setSandIsTriggered}
            setDeleteTrigger={setDeleteTrigger}
            setEditId={setEditId}
            setDeleteFields={setDeleteFields}
          />
        }
        {!hideComments && <ProjectComments />}
      </div>
      {formError && (
        <div className="row g-2">
          <h5 className="error">{formError}</h5>
        </div>
      )}
      {!loadsPending && currentProject && (
        <>
          <div className="row g-2 totals">
            <div className="d-flex justify-content-between card-header">
              <h4>Project Details</h4>
              <div>
                {!currentProject.project.archived && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => setCreditDebitTrigger(true)}
                  >
                    Add Credit/Debit
                  </button>
                )}
                {!showPTotals && (
                  <img
                    src={Show}
                    onClick={() => setShowPTotals(true)}
                    alt="show"
                    className="project-delete-image"
                  />
                )}
                {showPTotals && (
                  <img
                    src={Hide}
                    onClick={() => setShowPTotals(false)}
                    alt="hide"
                    className="project-delete-image"
                  />
                )}
              </div>
            </div>
          </div>
          {showPTotals && (
            <div className="row g-2 totals">
              <ProjectTotals
                setAddProjectRsTrigger={setAddProjectRsTrigger}
                setEditId={setEditId}
                setDeleteTrigger={setDeleteTrigger}
                setDeleteFields={setDeleteFields}
              />
              {terminalMileages && (
                <TerminalMileagesCard terminalMileages={terminalMileages} />
              )}
              <CreditDebitCard
                setEditTrigger={setCreditDebitTrigger}
                setDeleteTrigger={setDeleteTrigger}
                setEditId={setEditId}
                setDeleteFields={setDeleteFields}
              />
            </div>
          )}
          <div className="row g-2 totals">
            <div className="d-flex card-header">
              <h4>Fiscal Totals</h4>
              {!showFTotals && (
                <img
                  src={Show}
                  onClick={() => setShowFTotals(true)}
                  alt="show"
                  className="project-delete-image"
                />
              )}
              {showFTotals && (
                <img
                  src={Hide}
                  onClick={() => setShowFTotals(false)}
                  alt="hide"
                  className="project-delete-image"
                />
              )}
            </div>
            {showFTotals && <FiscalTotals />}
          </div>
          <div className="row g-2 totals">{showFTotals && <PoTotals />}</div>
        </>
      )}
      {!loadsPending && !customerInvsPending && !assetLoadsPending ? (
        <LoadTabs
          setLoadLockTrigger={setLoadLockTrigger}
          setIsAsset={setIsAsset}
          setLockEndpoint={setLockEndpoint}
        />
      ) : (
        <Loading />
      )}
      {archiveTrigger && !currentProject.project.archived ? (
        <ArchiveProject
          setTrigger={setArchiveTrigger}
          statusUpdate={"Archive"}
        />
      ) : (
        archiveTrigger &&
        currentProject.project.archived && (
          <ArchiveProject
            setTrigger={setArchiveTrigger}
            statusUpdate={"UnArchive"}
          />
        )
      )}
      {editIsTriggered && (
        <CreateProject
          isTriggered={editIsTriggered}
          setIsTriggered={setEditIsTriggered}
          dropDowns={dropDowns}
          projectId={currentProject.project.id}
        />
      )}
      {sandIsTriggered && (
        <SandPoPopup
          sandPoDD={purchaseOrderOptions}
          setSandIsTriggered={setSandIsTriggered}
          setEditId={setEditId}
          sandId={editId}
        />
      )}
      {creditDebitTrigger && (
        <AddCreditDebit
          setTrigger={setCreditDebitTrigger}
          setEditId={setEditId}
          docId={editId}
        />
      )}
      {deleteTrigger && deleteFields && (
        <DeletePopup
          setTrigger={deleteFields.setTrigger}
          id={deleteFields.id}
          table={deleteFields.table}
          endpoint={deleteFields.endpoint}
          nameField={deleteFields.nameField}
          projectId={currentProject.project.id}
          setDocId={setEditId}
          setStates={deleteFields.setStates}
          setStateFields={deleteFields.setStateFields}
        />
      )}
      {loadLockTrigger && (
        <LockLoad
          setLoadLockTrigger={setLoadLockTrigger}
          endpoint={lockEndpoint}
          isAsset={isAsset}
        />
      )}
      {addCarrierTrigger && currentProject && (
        <AddCarrier
          setTrigger={setAddCarrierTrigger}
          carrierId={editId}
          setCarrierId={setEditId}
        />
      )}
      {addProjectRsTrigger && (
        <AddProjectRateSheet setAddProjectRsTrigger={setAddProjectRsTrigger} pRsId={editId} setEditId={setEditId}/>
      )}
    </div>
  ) : (
    <div className="row g-2">
      <Loading />
    </div>
  );
}
